import { useMemo } from 'react';

import useQueryStringParameters from '../../utils/useQueryStringParameters';
import { useGetGscConnectionStatusQuery } from './api/gscConnectionApi';
import {
  resolveSelectedProperty,
  toSelectableProperties,
  type SelectableProperty,
} from './SearchPerformanceProperties';
import {
  buildSearchPerformanceQueryParameters,
  type SearchPerformanceQueryParams,
} from './SearchPerformanceQueryParameters';

export type SearchPerformanceProperty = {
  /** Every property the dashboard can show data for, for the picker. */
  properties: SelectableProperty[];
  isLoading: boolean;
  /**
   * The property the dashboard is reporting on — `undefined` only while
   * loading, or when the tenant has no property with data. Carries the GSC and
   * PRERENDER source ids, which is what a widget needs to query its dataset.
   */
  selectedProperty: SelectableProperty | undefined;
  selectProperty: (propertyUrl: string) => void;
};

/**
 * The single answer to "which property is the dashboard reporting on".
 *
 * Every consumer must go through this rather than reading the `property` query
 * param: the param is absent until the user picks something explicitly, so a
 * widget reading the URL directly would see nothing while the picker shows the
 * default. Resolution lives here so the filter row and the widgets can never
 * disagree.
 *
 * Unlike `useGscConnectionFlow` — which must be called once because it owns
 * local state — this hook is safe to call from as many components as need it:
 * it is a pure derivation over the shared RTK Query cache entry and the URL, so
 * every instance resolves to the same value and the status request is made once.
 */
function useSearchPerformanceProperty(): SearchPerformanceProperty {
  const { data, isLoading } = useGetGscConnectionStatusQuery();
  const { queryStrings, updateMultipleParameters } = useQueryStringParameters<SearchPerformanceQueryParams>(
    buildSearchPerformanceQueryParameters
  );

  const properties = useMemo(() => toSelectableProperties(data?.sources ?? []), [data?.sources]);

  const selectedProperty = resolveSelectedProperty(properties, queryStrings.property);

  return {
    properties,
    isLoading,
    selectedProperty,
    // Switching property is a fresh view: drop the previous property's Pages tab
    // and URL search along with it. (The path table's sort lives in React state
    // and resets in PerformanceByPathSection on the same change.)
    selectProperty: (propertyUrl: string) =>
      updateMultipleParameters({ property: propertyUrl, tab: undefined, search: undefined }),
  };
}

export default useSearchPerformanceProperty;
